import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { PowerBiReportsService } from '../Services/powerbi-reports.service';
import { PowerBiEmbedConfig } from '../Models/common.model';
import * as Global from '../Models/constants';
import { factories, models, service, Report } from 'powerbi-client';

@Component({
    selector: 'powerbi-report',
    templateUrl: './powerbi-report.component.html'
})
export class PowerBiReportComponent implements AfterViewInit, OnDestroy {
    @ViewChild('powerBiContainer') powerBiContainer?: ElementRef<HTMLDivElement>;

    loadingMessage = 'Loading Power BI report...';
    isLoading = true;
    powerBiError = '';
    powerBiEmbedConfig: PowerBiEmbedConfig | null = null;
    
    private readonly powerBiService = new service.Service(
        factories.hpmFactory,
        factories.wpmpFactory,
        factories.routerFactory);

    private embeddedReport: Report | null = null;
    private refreshTimer: ReturnType<typeof setTimeout> | null = null;
    private isRefreshingToken = false;

    constructor(
        private readonly dataService: PowerBiReportsService,
        private readonly cdr: ChangeDetectorRef,
        private readonly ngZone: NgZone
    ) {
        Global.AddLogs('Power BI Reports constructor called');
    }

    ngAfterViewInit(): void {
        Global.AddLogs('Power BI Reports ngAfterViewInit method called');
        this.initializeReport();
    }

    ngOnDestroy(): void {
        this.clearRefreshTimer();
        if (this.powerBiContainer?.nativeElement) {
            this.powerBiService.reset(this.powerBiContainer.nativeElement);
        }

        this.embeddedReport = null;
    }

    private initializeReport(): void {
        this.clearRefreshTimer();
        this.isRefreshingToken = false;
        this.powerBiError = '';
        this.powerBiEmbedConfig = null;
        this.embeddedReport = null;
    
        if (this.powerBiContainer?.nativeElement) {
            this.powerBiService.reset(this.powerBiContainer.nativeElement);
        }

        this.isLoading = true;
        this.loadEmbedConfig();
    }

    private loadEmbedConfig(): void {
        Global.AddLogs('Power BI Reports loadEmbedConfig called');
        this.dataService.getEmbedConfig().subscribe(
            (data: PowerBiEmbedConfig) => {           
                if (!data || !data.ReportId || !data.EmbedUrl || !data.AccessToken) {
                    this.powerBiError = 'Unable to load the Power BI report.';
                    this.isLoading = false;
                    this.cdr.detectChanges();
                    return;
                }

                this.powerBiEmbedConfig = data;
                this.cdr.detectChanges();
                this.embedReport(data);
                this.scheduleRefreshToken(data.Expiration);
            },
            error => {
                Global.AddLogs('Power BI Reports getEmbedConfig exception logged :' + error);
                this.powerBiEmbedConfig = null;
                this.powerBiError = 'Unable to load the Power BI report.';
                this.isLoading = false;
                this.cdr.detectChanges();
            });
    }

    private scheduleRefreshToken(expirationUtc: string): void {
        if (!expirationUtc) {
            return;
        }

        const expirationTime = new Date(expirationUtc).getTime();
        if (Number.isNaN(expirationTime)) {
            return;
        }

        this.clearRefreshTimer();

        const refreshAt = expirationTime - 5 * 60 * 1000;
        const delay = Math.max(refreshAt - Date.now(), 5000);

        this.refreshTimer = setTimeout(() => {
            this.refreshToken();
        }, delay);
    }

    private refreshToken(): void {
        if (this.isRefreshingToken) {
            return;
        }

        this.isRefreshingToken = true;

        this.dataService.getEmbedConfig().subscribe(
            async (data: PowerBiEmbedConfig) => {
                try {
                    if (!data || !data.ReportId || !data.AccessToken || !data.Expiration) {
                        throw new Error('Invalid Power BI token refresh response.');
                    }

                    this.powerBiEmbedConfig = data;

                    if (this.embeddedReport) {
                        await this.embeddedReport.setAccessToken(data.AccessToken);
                    }

                    this.scheduleRefreshToken(data.Expiration);
                } catch (error) {
                    Global.AddLogs('Power BI token refresh failed: ' + JSON.stringify(error));
                } finally {
                    this.isRefreshingToken = false;
                }
            },
            error => {
                Global.AddLogs('Power BI getEmbedConfig refresh exception logged :' + error);
                this.isRefreshingToken = false;

                this.clearRefreshTimer();
                this.refreshTimer = setTimeout(() => this.refreshToken(), 30000);
            }
        );
    }

    private embedReport(config: PowerBiEmbedConfig): void {
        if (!this.powerBiContainer?.nativeElement) {
            this.powerBiError = 'Unable to load the Power BI report.';
            this.isLoading = false;
            this.cdr.detectChanges();
            return;
        }

        const embedConfig = {
            type: 'report',
            id: config.ReportId,
            embedUrl: config.EmbedUrl,
            accessToken: config.AccessToken,
            tokenType: models.TokenType.Embed,
            permissions: models.Permissions.All,
            settings: {
                panes: {
                    filters: {
                        visible: false
                    },
                    pageNavigation: {
                        visible: true
                    }
                },
                navContentPaneEnabled: true
            }
        };

        this.powerBiService.reset(this.powerBiContainer.nativeElement);
        this.embeddedReport = this.powerBiService.embed(this.powerBiContainer.nativeElement, embedConfig) as Report;
        this.ngZone.run(() => {
            this.powerBiError = '';
            this.isLoading = false;
            this.cdr.detectChanges();
        });
    }

    private clearRefreshTimer(): void {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
    }
}